import React, { useState, useRef } from 'react';
import { Upload, Eye, CheckCircle, XCircle, FileText } from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';
import FormField, { Input, Select } from '../components/ui/FormField';
import { useQuery } from '@tanstack/react-query';

export default function MarcImportPage() {
  const [preview, setPreview] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [copies, setCopies] = useState('1');
  const [collectionId, setCollectionId] = useState('');
  const [file, setFile] = useState<File|null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: colls } = useQuery({
    queryKey: ['collections'],
    queryFn: () => api.get('/books/collections'),
  } as any);

  const handleFile = async (f: File) => {
    if (!f.name.endsWith('.mrc') && !f.name.endsWith('.marc')) {
      return toast.error('მხოლოდ .mrc ან .marc ფაილი');
    }
    setFile(f); setLoading(true); setPreview([]); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await api.post('/marc/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(data.preview || []);
      setTotal(data.total || 0);
      toast.success(`${data.total} ჩანაწერი ნაპოვნია`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'ფაილის წაკითხვა ვერ მოხდა');
    } finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!file) return toast.error('ფაილი არ არის');
    setImporting(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('copies', copies);
      if (collectionId) fd.append('collection_id', collectionId);
      const { data } = await api.post('/marc/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      toast.success(`${data.imported} წიგნი დაემატა!`);
      setFile(null); setPreview([]);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'შეცდომა');
    } finally { setImporting(false); }
  };

  return (
    <div style={{padding:24,fontFamily:'system-ui,sans-serif',maxWidth:800}}>
      <div style={{marginBottom:24}}>
        <h1 style={{fontSize:20,fontWeight:600,color:'#f1f5f9',margin:0}}>MARC იმპორტი</h1>
        <p style={{fontSize:12,color:'#475569',marginTop:4}}>ISO 2709 / MARC21 ფაილიდან წიგნების მასობრივი ატვირთვა</p>
      </div>

      <div style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:12,padding:16,marginBottom:12}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
          <FormField label="ასლების რაოდენობა">
            <Input type="number" min="1" max="99" value={copies} onChange={e => setCopies(e.target.value)} />
          </FormField>
          <FormField label="კოლექცია">
            <Select value={collectionId} onChange={e => setCollectionId(e.target.value)}>
              <option value="">— კოლექცია —</option>
              {((colls as any)?.data?.collections||[]).map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </FormField>
        </div>

        <input ref={inputRef} type="file" accept=".mrc,.marc" style={{display:'none'}}
          onChange={e => { const f = e.target.files?.[0]; if(f) handleFile(f); }} />

        <div onClick={() => inputRef.current?.click()}
          style={{border:'2px dashed #1e2d45',borderRadius:10,padding:32,textAlign:'center',cursor:'pointer'}}
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor='#60a5fa'; }}
          onDragLeave={e => { e.currentTarget.style.borderColor='#1e2d45'; }}
          onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor='#1e2d45'; const f = e.dataTransfer.files[0]; if(f) handleFile(f); }}>
          {loading ? (
            <div>
              <div style={{width:32,height:32,border:'3px solid #1e2d45',borderTop:'3px solid #60a5fa',borderRadius:'50%',margin:'0 auto 12px',animation:'spin 1s linear infinite'}} />
              <p style={{color:'#64748b',fontSize:13}}>ანალიზდება...</p>
            </div>
          ) : file ? (
            <div>
              <FileText size={32} style={{color:'#60a5fa',margin:'0 auto 8px',display:'block'}} />
              <p style={{color:'#cbd5e1',fontSize:13,fontWeight:500}}>{file.name}</p>
              <p style={{color:'#475569',fontSize:11,marginTop:4}}>{total} ჩანაწერი</p>
            </div>
          ) : (
            <div>
              <Upload size={32} style={{color:'#334155',margin:'0 auto 12px',display:'block'}} />
              <p style={{color:'#475569',fontSize:13}}>MARC ფაილი ჩააგდე ან დააჭირე</p>
              <p style={{color:'#334155',fontSize:11,marginTop:4}}>.mrc · .marc</p>
            </div>
          )}
        </div>
      </div>

      {preview.length > 0 && (
        <div style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden',marginBottom:12}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid #1e2d45',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,fontSize:13,fontWeight:500,color:'#cbd5e1'}}>
              <Eye size={15} /> პრევიუ — სულ {total} ჩანაწერი
            </div>
            <button onClick={handleImport} disabled={importing}
              style={{display:'flex',alignItems:'center',gap:6,padding:'7px 16px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:12,cursor:'pointer',opacity:importing?0.6:1}}>
              <Upload size={13}/> {importing ? 'იმპორტი...' : `${total} ჩანაწერის Import`}
            </button>
          </div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{borderBottom:'1px solid #1e2d45'}}>
                {['სათაური','ავტორი','ISBN','გამომცემელი','წელი'].map(h => (
                  <th key={h} style={{textAlign:'left',padding:'8px 12px',fontSize:10,fontWeight:500,color:'#475569'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.map((b, i) => (
                <tr key={i} style={{borderBottom:'1px solid #0f1826'}}>
                  <td style={{padding:'8px 12px',color:'#cbd5e1',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{b.title||'—'}</td>
                  <td style={{padding:'8px 12px',color:'#64748b'}}>{b.author||'—'}</td>
                  <td style={{padding:'8px 12px',color:'#475569',fontFamily:'monospace',fontSize:10}}>{b.isbn||'—'}</td>
                  <td style={{padding:'8px 12px',color:'#475569'}}>{b.publisher||'—'}</td>
                  <td style={{padding:'8px 12px',color:'#475569'}}>{b.pub_year||'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result && (
        <div style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
          <div style={{padding:'14px 16px',display:'flex',alignItems:'center',gap:16}}>
            <div style={{display:'flex',alignItems:'center',gap:6}}>
              <CheckCircle size={16} style={{color:'#34d399'}} />
              <span style={{fontSize:13,color:'#34d399',fontWeight:500}}>დაემატა: {result.imported}</span>
            </div>
            {result.errors?.length > 0 && (
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <XCircle size={16} style={{color:'#f87171'}} />
                <span style={{fontSize:13,color:'#f87171'}}>შეცდომა: {result.errors.length}</span>
              </div>
            )}
          </div>
        </div>
      )}
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
